import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '../api/client';
import {
  agentSkill,
  createApiKey,
  listAgents,
  listApiKeys,
  revokeApiKey,
  usage as fetchUsage,
} from '../api/endpoints';
import { useAuth, useResource, useToast } from '../state';
import { formatLimit, formatTimestamp, formatRelative } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { CopyButton } from '../components/CopyButton';
import { ErrorNotice, InlineNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { LLM_MODELS, type LlmProviderId } from '@verifyloop/core';
import { ConfirmButton, SelectField, TextField } from '../components/Form';
import { loadSavedByok, saveByokLocally, type ByokSettings } from '../components/GenerateTestModal';
import { CUSTOM_MODEL_OPTION, modelSelection, resolveModelSelectValue } from '../lib/modelSelect';
import { Mono } from '../components/Pills';

export function SettingsPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const usage = useResource(() => fetchUsage(), [], { live: ['run.finished'] });

  return (
    <div className="page">
      <PageHeader
        title="Settings"
        subtitle="Credits, keys, plans, and the skill your coding agent reads."
      />

      {usage.error ? <ErrorNotice error={usage.error} onRetry={usage.reload} /> : null}
      {usage.loading ? <LoadingRows rows={3} label="Loading usage" /> : null}

      {usage.data ? (
        <>
          <section className="panel">
            <SectionTitle>Usage</SectionTitle>
            <dl className="fact-grid">
              <div>
                <dt>credits remaining</dt>
                <dd className="num">
                  {usage.data.creditsRemaining} <span className="muted">of {usage.data.creditsMonthly}</span>
                </dd>
              </div>
              <div>
                <dt>plan</dt>
                <dd>{usage.data.plan}</dd>
              </div>
              <div>
                <dt>environments</dt>
                <dd className="num">
                  {usage.data.environmentsUsed} /{' '}
                  {formatLimit(usage.data.environmentsAllowed, 'environment')}
                </dd>
              </div>
              <div>
                <dt>run history</dt>
                <dd className="num">{formatLimit(usage.data.runHistoryDays, 'days')}</dd>
              </div>
              <div>
                <dt>open tunnels</dt>
                <dd className="num">{usage.data.tunnels}</dd>
              </div>
            </dl>
            <div className="meter" role="img" aria-label={`${usage.data.creditsRemaining} of ${usage.data.creditsMonthly} credits remaining`}>
              <span
                className="meter__fill"
                style={{
                  width: `${
                    usage.data.creditsMonthly > 0
                      ? Math.max(0, Math.min(100, (usage.data.creditsRemaining / usage.data.creditsMonthly) * 100))
                      : 0
                  }%`,
                }}
              />
            </div>

            <h4 className="form-subhead">Credit ledger</h4>
            {usage.data.ledger.length === 0 ? (
              <p className="muted">Nothing charged yet.</p>
            ) : (
              <div className="table-wrap">
                <table className="table table--compact">
                  <caption className="visually-hidden">Recent credit activity</caption>
                  <thead>
                    <tr>
                      <th scope="col">when</th>
                      <th scope="col">reason</th>
                      <th scope="col">delta</th>
                      <th scope="col">run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.data.ledger.map((entry) => (
                      <tr key={entry.id}>
                        <td title={formatTimestamp(entry.at)}>{formatRelative(entry.at)}</td>
                        <td>{entry.reason}</td>
                        <td className={`num ${entry.delta < 0 ? 'delta--down' : 'delta--up'}`}>
                          {entry.delta > 0 ? '+' : ''}
                          {entry.delta}
                        </td>
                        <td>
                          {entry.runId ? <Mono>{entry.runId.slice(0, 12)}…</Mono> : <span className="muted">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <ApiKeysPanel />
          <ByokSettingsPanel />
          <PlansPanel plans={usage.data.plans} current={usage.data.plan} />
          <AdminLlmBanner isAdmin={isAdmin} />
        </>
      ) : null}

      <AgentsPanel />
    </div>
  );
}

function ApiKeysPanel(): JSX.Element {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const keys = useResource(() => listApiKeys(), []);

  const mint = async (): Promise<void> => {
    setPending(true);
    try {
      const response = await createApiKey(label || 'dashboard', []);
      setIssued(response.apiKey);
      toast.success('API key created', 'shown once: copy it now.');
      setLabel('');
      keys.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="panel">
      <SectionTitle>API keys</SectionTitle>
      <p className="muted">
        The key is the session. Give each machine its own so a leaked laptop token can be revoked without
        touching anyone else.
      </p>

      {issued ? (
        <div className="secret">
          <code className="secret__value">{issued}</code>
          <CopyButton value={issued} label="Copy key" />
          <button type="button" className="btn btn--ghost btn--small" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      ) : (
        <div className="row-fields">
          <TextField label="Label" value={label} onChange={setLabel} placeholder="laptop" />
          <button type="button" className="btn" onClick={() => void mint()} disabled={pending}>
            {pending ? 'Creating…' : 'Create key'}
          </button>
        </div>
      )}

      {keys.error ? <ErrorNotice error={keys.error} onRetry={keys.reload} /> : null}
      {keys.loading ? <LoadingRows rows={2} label="Loading keys" /> : null}

      {keys.data ? (
        <div className="table-wrap">
          <table className="table table--compact">
            <caption className="visually-hidden">API keys for this account</caption>
            <thead>
              <tr>
                <th scope="col">label</th>
                <th scope="col">scopes</th>
                <th scope="col">created</th>
                <th scope="col">last used</th>
                <th scope="col">id</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {keys.data.keys.map((key) => (
                <tr key={key.id} className={key.revokedAt ? 'is-muted' : undefined}>
                  <td>
                    {key.label}
                    {key.revokedAt ? <span className="pill pill--neutral pill--subtle">revoked</span> : null}
                  </td>
                  <td className="muted">{key.scopes.join(', ')}</td>
                  <td>{formatRelative(key.createdAt)}</td>
                  <td>{key.lastUsedAt ? formatRelative(key.lastUsedAt) : '-'}</td>
                  <td>
                    <Mono title={key.id}>{key.id.slice(0, 12)}…</Mono>
                  </td>
                  <td>
                    {key.revokedAt ? null : (
                      <ConfirmButton
                        label="Revoke"
                        confirmLabel="Really revoke?"
                        onConfirm={() => {
                          void revokeApiKey(key.id)
                            .then(() => {
                              toast.success(`Revoked “${key.label}”`);
                              keys.reload();
                            })
                            .catch((error: unknown) => toast.error(describeError(error)));
                        }}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function PlansPanel({
  plans,
  current,
}: {
  plans: Record<string, { name: string; label: string; monthlyPriceUsd: number; monthlyCredits: number; environments: number | 'unlimited'; runHistoryDays: number | 'unlimited'; seats: number; features: string[] }>;
  current: string;
}): JSX.Element {
  return (
    <section className="panel">
      <SectionTitle>Plans</SectionTitle>
      <div className="table-wrap">
        <table className="table">
          <caption className="visually-hidden">Plan comparison, served by the server&apos;s own PLANS record</caption>
          <thead>
            <tr>
              <th scope="col">plan</th>
              <th scope="col">price</th>
              <th scope="col">credits</th>
              <th scope="col">environments</th>
              <th scope="col">history</th>
              <th scope="col">seats</th>
              <th scope="col">includes</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(plans).map((plan) => (
              <tr key={plan.name} className={plan.name === current ? 'is-current' : undefined}>
                <td>
                  <strong>{plan.label}</strong>
                  {plan.name === current ? <span className="pill pill--pass pill--subtle">current</span> : null}
                </td>
                <td className="num">${plan.monthlyPriceUsd}</td>
                <td className="num">{plan.monthlyCredits}</td>
                <td className="num">{formatLimit(plan.environments, 'environment')}</td>
                <td className="num">{formatLimit(plan.runHistoryDays, 'days')}</td>
                <td className="num">{plan.seats}</td>
                <td className="muted">{plan.features.join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AgentsPanel(): JSX.Element {
  const toast = useToast();
  const agents = useResource(() => listAgents(), []);
  const skill = useResource(() => agentSkill('default'), []);

  const renderedSkill = useMemo(() => {
    if (!skill.data) return '';
    if (typeof window === 'undefined') return skill.data;
    const origin = window.location.origin;
    // Adapt the displayed server URL to the active web origin if it's currently showing internal 0.0.0.0 or 127.0.0.1
    return skill.data.replace(/Server: `https?:\/\/(?:0\.0\.0\.0|127\.0\.0\.1|localhost)(?::\d+)?`/g, `Server: \`${origin}\``);
  }, [skill.data]);

  return (
    <section className="panel">
      <SectionTitle>Coding agents</SectionTitle>
      <p className="muted">
        Justloop ships one skill, wrapped for each agent&apos;s conventions. The server decides the paths;
        the CLI writes the files.
      </p>

      {agents.error ? <ErrorNotice error={agents.error} onRetry={agents.reload} /> : null}
      {agents.loading ? <LoadingRows rows={3} label="Loading agents" /> : null}

      {agents.data ? (
        <div className="table-wrap">
          <table className="table table--compact">
            <caption className="visually-hidden">Supported coding agents and where their skill lands</caption>
            <thead>
              <tr>
                <th scope="col">agent</th>
                <th scope="col">id</th>
                <th scope="col">skill path</th>
                <th scope="col">detected by</th>
              </tr>
            </thead>
            <tbody>
              {agents.data.agents.map((agent) => (
                <tr key={agent.id}>
                  <td>
                    <strong>{agent.label}</strong>
                  </td>
                  <td>
                    <Mono>{agent.id}</Mono>
                  </td>
                  <td>
                    <Mono>{agent.skillPath}</Mono>
                  </td>
                  <td className="muted">{agent.detectionPaths.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <h4 className="form-subhead">The verification-loop skill</h4>
      {skill.error ? <ErrorNotice error={skill.error} onRetry={skill.reload} /> : null}
      {skill.loading ? <LoadingRows rows={4} label="Loading skill" /> : null}
      {skill.data ? (
        <>
          <div className="row__actions">
            <CopyButton value={renderedSkill} label="Copy skill" />
            <InlineNotice tone="info">
              Install it with <code>justloop setup</code> (the same text the agent will read).
            </InlineNotice>
          </div>
          <pre className="log log--skill">{renderedSkill}</pre>
        </>
      ) : null}

      {agents.data && agents.data.agents.length > 0 ? (
        <p className="muted">
          {agents.data.agents.length} agents supported.{' '}
          <button
            type="button"
            className="linkish"
            onClick={() => {
              const paths = agents.data?.agents.map((agent) => agent.skillPath) ?? [];
              void navigator.clipboard?.writeText(paths.join('\n'));
              toast.info('Copied the list of skill paths');
            }}
          >
            Copy every skill path
          </button>
        </p>
      ) : null}
    </section>
  );
}

function AdminLlmBanner({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  return (
    <section className="panel" style={{ marginTop: '20px' }}>
      <SectionTitle
        actions={
          <Link to="/admin/llm" className="btn btn--small">
            Open Admin LLM →
          </Link>
        }
      >
        Super Admin: Hosted LLM
      </SectionTitle>
      <p className="muted" style={{ margin: 0, fontSize: '0.92rem' }}>
        Configure the operator-hosted AI model (OpenAI, Anthropic, Gemini, or custom endpoint) used for automatic test plan generation.
        {isAdmin
          ? ' Your account has super-admin privileges.'
          : ' Restricted to instance operators.'}
      </p>
    </section>
  );
}

function ByokSettingsPanel(): JSX.Element {
  const toast = useToast();
  const [settings, setSettings] = useState<ByokSettings>(() => loadSavedByok());
  const [customModel, setCustomModel] = useState(false);
  const [keyInput, setKeyInput] = useState(() => loadSavedByok().apiKey);

  const currentModels = LLM_MODELS[settings.provider] ?? LLM_MODELS.anthropic;
  const modelOptions = [
    ...currentModels.map((m) => ({ value: m.value, label: m.label })),
    { value: CUSTOM_MODEL_OPTION, label: 'Other / Custom Model Name...' },
  ];
  const selectValue = resolveModelSelectValue(currentModels, settings.model, customModel);

  const handleModelChange = (value: string) => {
    const selection = modelSelection(value);
    setCustomModel(selection.isCustom);
    setSettings({ ...settings, model: selection.model });
  };

  const save = () => {
    saveByokLocally({
      ...settings,
      apiKey: keyInput,
      saveLocally: true,
    });
    setSettings((prev) => ({ ...prev, apiKey: keyInput, saveLocally: true }));
    toast.success('BYOK preferences saved to this browser');
  };

  const clear = () => {
    saveByokLocally({
      ...settings,
      apiKey: '',
      saveLocally: false,
    });
    setKeyInput('');
    setSettings((prev) => ({ ...prev, apiKey: '', saveLocally: false }));
    toast.info('BYOK preferences cleared from this browser');
  };

  return (
    <section className="panel">
      <SectionTitle
        actions={
          keyInput ? (
            <button type="button" className="btn btn--small btn--ghost" onClick={clear}>
              Clear saved key
            </button>
          ) : null
        }
      >
        AI Test Generation: Bring Your Own Key (BYOK)
      </SectionTitle>
      <p className="muted" style={{ fontSize: '0.88rem', margin: '0 0 16px' }}>
        Supply your own AI API key (Anthropic, OpenAI, Gemini, or custom endpoint) to draft test plans from PRD requirements with <strong>0 credit charge</strong>. Keys are stored solely in your local browser storage and never saved to the server database.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
        <SelectField
          label="Default Provider"
          value={settings.provider}
          onChange={(val) => {
            const p = val as LlmProviderId;
            const def = LLM_MODELS[p]?.[0]?.value ?? '';
            setCustomModel(false);
            setSettings({ ...settings, provider: p, model: def });
          }}
          options={[
            { value: 'anthropic', label: 'Anthropic (Claude)' },
            { value: 'openai', label: 'OpenAI (GPT)' },
            { value: 'google', label: 'Google (Gemini)' },
            { value: 'custom', label: 'Custom (OpenAI-compatible)' },
          ]}
        />
        <SelectField
          label="Default Model"
          value={selectValue}
          onChange={handleModelChange}
          options={modelOptions}
        />
      </div>

      {selectValue === '__custom__' ? (
        <div style={{ marginTop: '10px' }}>
          <TextField
            label="Specify Custom Model Name"
            value={settings.model}
            onChange={(model) => setSettings({ ...settings, model })}
            placeholder="e.g. meta-llama/Llama-3.3-70B-Instruct"
            hint="Enter the exact model identifier accepted by your provider or proxy endpoint."
            required
          />
        </div>
      ) : null}

      <div style={{ marginTop: '10px' }}>
        <TextField
          label="Your Secret API Key"
          type="password"
          value={keyInput}
          onChange={setKeyInput}
          placeholder={settings.apiKey ? '••••••••••••••••' : 'Enter API Key (sk-...)'}
          autoComplete="new-password"
          hint="Stored in your local browser localStorage. Sent only when you request an AI plan generation."
        />
      </div>

      <div style={{ marginTop: '14px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button type="button" className="btn btn--small" onClick={save}>
          Save BYOK to Browser
        </button>
        {settings.apiKey ? (
          <span style={{ fontSize: '12px', color: 'var(--pass)', fontWeight: 600 }}>
            ✓ Ready for 0-credit test generation
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>
            No BYOK key stored (hosted model will be used by default)
          </span>
        )}
      </div>
    </section>
  );
}